import * as React from "react";
import { Badge, Grid, IconButton, makeStyles, Drawer, MenuList, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { matchPath } from "react-router";
import { Avatar } from "src/common/components";
import { IconFeedback, IconLogout, IconPeople, IconPrint, IconRemove } from "src/common/icons";
import { IExtendedUser, IMyOpenTasks, IAppReduxState } from "src/common/types";
import { getFullName, removeToken } from "src/helpers";
import { DEFAULT_LOCALE } from "src/config/locale";
import { LocalesContext } from "src/common/contexts";
import Languages from "../Header/Languages";
import MenuItem from "../../SideMenu/MenuItem";

const useStyles = makeStyles((theme: any) => ({
  badgeLabel: {
    '& [class*="icon-"]': {
      fontSize: "1.6rem"
    },
    margin: "4px -4px 0 0"
  },
  badgeRoot: {
    margin: "0 18px 0 0"
  },
  closeIcon: {
    color: "#fff",
    fontSize: "1.4rem"
  },
  closeIconContainer: {
    paddingRight: 14
  },
  contractTitle: {
    color: "#fff",
    fontSize: "1.2rem",
    lineHeight: "1.9rem",
    marginBottom: 4
  },
  drawerPaper: {
    [theme.breakpoints.up("md")]: {
      display: "none"
    },
    background: "fff",
    border: "none",
    width: 264
  },
  fullName: {
    color: "#fff",
    fontSize: "1.6rem",
    fontWeight: 600,
    lineHeight: "2.4rem",
    margin: "9px 0 3px"
  },
  languagesContainer: { color: "#fff", padding: "7px 10px" },
  logoutBtn: {
    fontSize: "15px",
    padding: 0,
    textTransform: "none" as "none"
  },
  logoutText: {
    fontSize: "15px",
    marginLeft: "13px"
  },
  lowerContainer: {
    borderTop: "1px solid #e6e6e6",
    fontSize: "15px",
    height: "56px",
    padding: "16px 0 0 10px"
  },
  mainContainer: {
    display: "flex",
    flexDirection: "column" as "column",
    height: "100%"
  },
  menu: {
    "& > a, & > div": {
      paddingLeft: 12
    },
    fontSize: "15px"
  },
  menuLabel: {
    marginRight: "7px"
  },
  middleContainer: {
    flexGrow: 1,
    padding: "24px 0 0"
  },
  profileDetailsContainer: {
    display: "flex",
    justifyContent: "space-between"
  },
  topContainer: {
    background: theme.palette.primary.main,
    display: "flex",
    flexDirection: "column" as "column",
    padding: "16px 0 16px 16px"
  },
  topRowContainer: {
    display: "flex",
    justifyContent: "space-between"
  }
}));

interface IRouteParams {
  lng: string;
  view: string;
}

interface IOwnProps {
  history: {
    push: (arg0: string) => void;
    replace: (arg0: string) => void;
  };
  onClose: () => void;
  open: boolean;
}

interface IMapStateToProps {
  currentUser: IExtendedUser;
  hasEmployees: boolean;
  openTasks: IMyOpenTasks;
  printVisible: boolean;
}

type Props = IOwnProps & IMapStateToProps;

const MobileMenu = ({
  currentUser: { photoBase64, title, department, subDepartment, firstName, lastName, middleName, userRole },
  hasEmployees,
  history,
  open,
  printVisible,
  onClose,
  openTasks: { myEmployeesCount, myFeedbacksCount, peersCount }
}: Props) => {
  const fullName = getFullName([firstName, middleName, lastName]);
  const classes = useStyles();
  const { t } = useTranslation();
  const { locales } = React.useContext(LocalesContext);

  const position = [department, subDepartment].filter(value => value).join(", ");

  const handlePrint = () => window.print();

  const logout = () => {
    removeToken();
    sessionStorage.removeItem("nextUrl");
    window.location.href = `/oidcoauth2?logout=${window.location}`;
  };

  const match = matchPath<IRouteParams>(location.pathname, {
    exact: false,
    path: "/:lng/dashboard/:view",
    strict: false
  });

  const lng = (match && match.params.lng) || DEFAULT_LOCALE;
  const currentPath = (match && match.params.view) || "my-feedbacks";

  const badgeClasses = {
    badge: classes.badgeLabel,
    root: classes.badgeRoot
  };

  // Close drawer on navigation
  const handleNavClick = React.useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Drawer
      classes={{ paper: classes.drawerPaper }}
      onBackdropClick={onClose}
      open={open}
      anchor="right"
      variant="temporary"
    >
      <Grid className={classes.mainContainer}>
        <Grid item className={classes.topContainer}>
          <Grid className={classes.topRowContainer}>
            <Grid>
              <Avatar type="medium" avatar={photoBase64} fullName={fullName} />
            </Grid>
            <Grid className={classes.closeIconContainer}>
              <IconButton className={classes.closeIcon} onClick={onClose}>
                <IconRemove />
              </IconButton>
            </Grid>
          </Grid>
          <Grid className={classes.profileDetailsContainer}>
            <Grid>
              <Typography className={classes.fullName}>{fullName}</Typography>
              <Typography className={classes.contractTitle} variant="body1">
                {position}
              </Typography>
              <Typography className={classes.contractTitle} variant="body1">
                {title}
              </Typography>
            </Grid>
            <Grid className={classes.languagesContainer}>
              <Languages location={location} history={history} lngs={locales} />
            </Grid>
          </Grid>
        </Grid>
        <Grid item className={classes.middleContainer}>
          <MenuList className={classes.menu}>
            {printVisible && (
              <MenuItem onClick={handlePrint} selected={false}>
                <Badge classes={badgeClasses} invisible color="secondary" badgeContent={0}>
                  <IconPrint />
                </Badge>
                {t(`feedback.header.print`)}
              </MenuItem>
            )}
            {hasEmployees && (
              <MenuItem
                onClick={handleNavClick}
                selected={currentPath === "my-employees"}
                to={`/${lng}/dashboard/my-employees`}
              >
                <Badge
                  classes={badgeClasses}
                  invisible={myEmployeesCount === 0}
                  color="secondary"
                  badgeContent={myEmployeesCount}
                >
                  <IconPeople />
                </Badge>
                <span className={classes.menuLabel}>{t("feedback.sidemenu.my-employees")}</span>
              </MenuItem>
            )}
            <MenuItem
              onClick={handleNavClick}
              selected={currentPath === "my-feedbacks"}
              to={`/${lng}/dashboard/my-feedbacks`}
            >
              <Badge
                classes={badgeClasses}
                invisible={myFeedbacksCount === 0}
                color="secondary"
                badgeContent={myFeedbacksCount}
              >
                <IconFeedback />
              </Badge>
              <span className={classes.menuLabel}>{t("feedback.sidemenu.my-feedbacks")}</span>
            </MenuItem>

            <MenuItem onClick={handleNavClick} selected={currentPath === "my-peers"} to={`/${lng}/dashboard/my-peers`}>
              <Badge classes={badgeClasses} invisible={peersCount === 0} color="secondary" badgeContent={peersCount}>
                <IconPeople />
              </Badge>
              <span className={classes.menuLabel}>{t("feedback.sidemenu.myPeers")}</span>
            </MenuItem>
          </MenuList>
        </Grid>
        <Grid item className={classes.lowerContainer}>
          <IconButton className={classes.logoutBtn} onClick={logout}>
            <IconLogout />
            <Typography variant="body1" className={classes.logoutText}>
              Log out
            </Typography>
          </IconButton>
        </Grid>
      </Grid>
    </Drawer>
  );
};

const mapStateToProps = ({
  currentUser: { userData, openTasks },
  talkSheet: { talkSheet }
}: IAppReduxState): IMapStateToProps => ({
  currentUser: userData as IExtendedUser,
  hasEmployees: Boolean((userData as IExtendedUser).subordinatesCount),
  openTasks,
  printVisible: !!talkSheet
});

export default React.memo(connect(mapStateToProps)(MobileMenu));
